import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Clock3, MapPin, Sun } from "lucide-react";
import { STATS } from "@/lib/data";
import { usePrayerTimes } from "@/hooks/usePrayerTimes";
import { useScrollTo } from "@/hooks/useLenis";
import { EASE } from "@/components/Reveal";

const LINES = [
  { text: "Di mana ombak", italic: false },
  { text: "bertasbih,", italic: true },
  { text: "dan umat bersujud.", italic: false },
];

const Waves = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
    <div className="absolute -right-40 -top-40 h-[38rem] w-[38rem] rounded-full bg-cyan/10 blur-3xl" />
    <div className="absolute -left-32 bottom-0 h-[30rem] w-[30rem] rounded-full bg-gold/20 blur-3xl" />
    <svg className="absolute bottom-0 left-0 w-[140%] animate-drift text-azure/15" viewBox="0 0 1440 220" fill="none" preserveAspectRatio="none">
      <path d="M0 140C240 80 480 200 720 140S1200 80 1440 140" stroke="currentColor" strokeWidth="1.4" />
      <path d="M0 170C240 110 480 230 720 170S1200 110 1440 170" stroke="currentColor" strokeWidth="1.2" />
      <path d="M0 200C240 140 480 260 720 200S1200 140 1440 200" stroke="currentColor" strokeWidth="1" />
    </svg>
  </div>
);

const RotatingBadge = () => (
  <div className="absolute -left-6 -top-6 h-28 w-28 sm:-left-10 sm:-top-10 sm:h-36 sm:w-36" data-testid="hero-rotating-badge">
    <svg viewBox="0 0 200 200" className="h-full w-full animate-spin-slow">
      <defs>
        <path id="hero-circle" d="M100,100 m-72,0 a72,72 0 1,1 144,0 a72,72 0 1,1 -144,0" />
      </defs>
      <text className="fill-navy font-grotesk text-[15.5px] font-bold uppercase tracking-[0.28em]">
        <textPath href="#hero-circle">Masjid Pantai Bali • Jembrana • 1961 •</textPath>
      </text>
    </svg>
    <div className="absolute inset-0 grid place-items-center">
      <span className="grid h-12 w-12 place-items-center rounded-full bg-gold text-navy shadow-lg shadow-gold/40">
        <Sun className="h-5 w-5" />
      </span>
    </div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const scrollTo = useScrollTo();
  const { next, countdown } = usePrayerTimes();
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 900], [0, 150]);
  const textY = useTransform(scrollY, [0, 900], [0, -70]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 60, damping: 18 });
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="beranda"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative min-h-screen overflow-hidden bg-sand-50 pb-14 pt-32 lg:pt-40"
      data-testid="hero-section"
    >
      <Waves />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-6 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="eyebrow inline-flex items-center gap-2 text-teal"
            data-testid="hero-eyebrow"
          >
            <MapPin className="h-3.5 w-3.5" /> Masjid Raudhatul Jannah · Desa Cupel, Jembrana · Est. 1961
          </motion.p>

          <h1
            className="mt-7 font-display text-5xl leading-[0.98] tracking-tight text-navy sm:text-6xl lg:text-7xl xl:text-[6.6rem]"
            data-testid="hero-headline"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="-mb-[0.1em] block overflow-hidden pb-[0.16em]">
                <motion.span
                  className={`block ${line.italic ? "italic text-azure" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.15, ease: EASE, delay: 0.3 + i * 0.14 }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-xl text-sm leading-relaxed text-slate-600 md:text-lg"
            data-testid="hero-subheadline"
          >
            Masjid Pantai Bali berdiri di tebing pesisir Cupel, Jembrana—menghadap Selat Bali dan matahari terbenam. Pusat
            ibadah, manasik haji terpadu pertama di Bali, dan ruang syiar budaya pesisir yang rahmatan lil 'alamin.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button onClick={() => scrollTo("#jadwal")} className="btn-primary" data-testid="hero-cta-prayer">
              <Clock3 className="h-4 w-4" /> Lihat Jadwal Sholat
            </button>
            <button onClick={() => scrollTo("#donasi")} className="btn-accent" data-testid="hero-cta-donate">
              Donasi & Infaq
            </button>
          </motion.div>

          {next && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
              className="glass mt-10 inline-flex items-center gap-4 rounded-full py-2.5 pl-3 pr-6"
              data-testid="hero-next-prayer"
            >
              <span className="relative grid h-9 w-9 place-items-center rounded-full bg-navy text-gold">
                <span className="absolute inset-0 animate-ping rounded-full bg-gold/30" />
                <Clock3 className="relative h-4 w-4" />
              </span>
              <span className="text-sm text-navy">
                <span className="eyebrow block text-[9px] text-teal">Sholat berikutnya</span>
                <span className="font-semibold">{next.name} · {next.time} WITA</span>
                <span className="ml-2 font-grotesk tabular-nums text-slate-500">dalam {countdown}</span>
              </span>
            </motion.div>
          )}
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: imgY, rotateX, rotateY, transformPerspective: 1200 }} className="relative will-change-transform">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 2rem 2rem)" }}
              animate={{ clipPath: "inset(0 0 0 0 round 999px 999px 2rem 2rem)" }}
              transition={{ duration: 1.5, ease: EASE, delay: 0.55 }}
              className="relative aspect-[4/5] overflow-hidden bg-navy"
              data-testid="hero-image-frame"
            >
              <motion.img
                src="/images/bpkh-2.jpg"
                alt="Pemandangan udara Masjid Pantai Bali di tepi Selat Bali, Jembrana"
                className="h-full w-full object-cover"
                initial={{ scale: 1.25 }}
                animate={{ scale: 1.05 }}
                transition={{ duration: 2.2, ease: EASE, delay: 0.55 }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
              <div className="absolute inset-x-6 bottom-6 text-sand-50">
                <p className="eyebrow text-gold">Kawasan 1 hektare</p>
                <p className="mt-1 font-display text-2xl leading-tight">Tebing pesisir Cupel, menghadap Selat Bali & senja.</p>
              </div>
            </motion.div>
            <RotatingBadge />
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
              className="glass absolute -right-4 top-1/3 hidden max-w-[12rem] rounded-3xl p-4 text-navy sm:block lg:-right-10"
              data-testid="hero-floating-card"
            >
              <p className="font-grotesk text-3xl font-extrabold">4×5 m</p>
              <p className="mt-1 text-xs leading-snug text-slate-600">Miniatur Ka'bah di area manasik haji seluas 40 are.</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.1, delayChildren: 1.25 } } }}
        className="relative mx-auto mt-16 max-w-[1400px] px-6 lg:mt-24 lg:px-12"
        data-testid="hero-stats"
      >
        <div className="grid grid-cols-2 border-t border-navy/10 md:grid-cols-4">
          {STATS.map((s) => (
            <motion.div
              key={s.label}
              variants={{ hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } } }}
              className="border-navy/10 py-6 pr-6 md:border-r md:last:border-r-0 md:[&:not(:first-child)]:pl-6"
            >
              <div className="font-grotesk text-4xl font-extrabold tracking-tight text-navy lg:text-5xl">{s.value}</div>
              <div className="mt-1 text-sm text-slate-500">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("#tentang")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-2 text-xs text-navy/60 lg:flex"
        data-testid="hero-scroll-indicator"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" /> Gulir untuk menjelajah
      </motion.button>
    </section>
  );
};
